import ipaddr from "ipaddr.js";
import { allowedCidrBlocks } from "./allowedCidrBlocks";

const BLOCKED_RANGES = new Set([
  "loopback",
  "private",
  "linkLocal",
  "uniqueLocal",
  "unspecified",
]);

function isBlockedAddress(address: string): boolean {
  const addr = ipaddr.parse(address);
  // The deployment allowlist takes precedence over every block, including
  // loopback/link-local/metadata (mirrors the pony guard's `is_hard_blocked`).
  if (
    allowedCidrBlocks().some(
      ([net, bits]) => addr.kind() === net.kind() && addr.match(net, bits)
    )
  ) {
    return false;
  }
  return BLOCKED_RANGES.has(addr.range());
}

// Synchronous shape check for contexts that can't do DNS (shared mutators run on
// the client too): require HTTPS and reject localhost / literal private-IP
// hosts. The authoritative guard is the async `resolveExternalUrl` at fetch
// time, which resolves DNS and pins the IP; this is fast-fail feedback.
export function isPublicHttpsUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^\[|]$/g, "");
    if (parsed.protocol !== "https:" || host === "localhost") return false;
    return !ipaddr.isValid(host) || !isBlockedAddress(host);
  } catch {
    return false;
  }
}

export type ResolvedExternalUrl = {
  parsed: URL;
  addresses: Array<{ address: string; family: 4 | 6 }>;
};

export async function validateExternalUrl(url: string): Promise<string | null> {
  const result = await resolveExternalUrl(url);
  return "reason" in result ? result.reason : null;
}

export async function resolveExternalUrl(
  url: string
): Promise<ResolvedExternalUrl | { reason: string }> {
  const parsed = new URL(url);
  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    return { reason: "URL scheme must be http or https" };
  }
  try {
    const host = parsed.hostname.replace(/^\[|]$/g, "");
    const results = await Bun.dns.lookup(host);
    for (const { address } of results) {
      if (isBlockedAddress(address)) {
        return {
          reason: "URL must not resolve to a private or internal address",
        };
      }
    }
    const addresses = results.map(({ address }) => ({
      address,
      family:
        ipaddr.parse(address).kind() === "ipv6" ? (6 as const) : (4 as const),
    }));
    return { parsed, addresses };
  } catch {
    return { reason: `Could not resolve hostname: ${parsed.hostname}` };
  }
}
